import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, Loader2, Clock, CheckCircle, Camera, Building2, Printer } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    company_name: '',
    user_type: 'photographer', // photographer veya salon_owner
    role: 'photographer'
  });
  const [pendingApproval, setPendingApproval] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const { register, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectUserType = (type) => {
    setFormData({ ...formData, user_type: type, role: type === 'albumcu' ? 'albumcu' : 'photographer' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    if (!formData.company_name || !formData.company_name.trim()) {
      toast.error('Firma/Salon ismi zorunludur');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      const result = await register(formData);
      // Eğer pending_approval döndüyse onay ekranını göster
      if (result?.status === 'pending_approval') {
        setPendingApproval(true);
        setVerifySent(!!result.email_verification_sent);
        toast.success('Kayıt başarılı! Admin onayı bekleniyor.');
      } else {
        toast.success('Kayıt başarılı!');
        // Kullanıcı tipine göre yönlendir
        if (formData.user_type === 'salon_owner') {
          navigate('/salon');
        } else {
          navigate('/panel');
        }
      }
    } catch (error) {
      toast.error(error.message || 'Kayıt başarısız');
    }
  };

  // Onay bekliyor ekranı
  if (pendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <Heart className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold gradient-text">WeddingTakip</span>
            </Link>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm text-center" data-testid="pending-approval-message">
            <div className="h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-6">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Hesabınız Onay Bekliyor</h2>
            <p className="text-muted-foreground mb-6">
              Kaydınız başarıyla alındı. {verifySent ? 'E-posta adresinize gönderilen onay linkine tıklayın. ' : ''}Hesabınız admin onayından sonra aktif olacaktır. 
              Onay verildiğinde giriş yapabilirsiniz.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>E-posta: <strong>{formData.email}</strong></span>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/giris">
                <Button variant="outline" className="w-full">
                  Giriş Sayfasına Dön
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Heart className="h-8 w-8 text-primary" />
            <span className="text-2xl font-bold gradient-text">WeddingTakip</span>
          </Link>
          <h1 className="text-2xl font-semibold mb-2">Hesap Oluşturun</h1>
          <p className="text-muted-foreground">Ücretsiz kayıt olun ve hemen başlayın</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
          {/* Kullanıcı Tipi Seçimi */}
          <div className="mb-6">
            <Label className="mb-3 block">Hesap Türü *</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => selectUserType('photographer')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  formData.user_type === 'photographer'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="utype-photographer"
              >
                <Camera className={`h-6 w-6 mx-auto mb-1 ${
                  formData.user_type === 'photographer' ? 'text-primary' : 'text-muted-foreground'
                }`} />
                <p className={`text-xs font-medium ${
                  formData.user_type === 'photographer' ? 'text-primary' : ''
                }`}>Fotoğrafçı</p>
              </button>
              
              <button
                type="button"
                onClick={() => selectUserType('salon_owner')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  formData.user_type === 'salon_owner'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="utype-salon"
              >
                <Building2 className={`h-6 w-6 mx-auto mb-1 ${
                  formData.user_type === 'salon_owner' ? 'text-primary' : 'text-muted-foreground'
                }`} />
                <p className={`text-xs font-medium ${
                  formData.user_type === 'salon_owner' ? 'text-primary' : ''
                }`}>Düğün Salonu</p>
              </button>

              <button
                type="button"
                onClick={() => selectUserType('albumcu')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  formData.user_type === 'albumcu'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="utype-albumcu"
              >
                <Printer className={`h-6 w-6 mx-auto mb-1 ${
                  formData.user_type === 'albumcu' ? 'text-primary' : 'text-muted-foreground'
                }`} />
                <p className={`text-xs font-medium ${
                  formData.user_type === 'albumcu' ? 'text-primary' : ''
                }`}>Albümcü</p>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="full_name">Ad Soyad *</Label>
              <Input
                id="full_name"
                name="full_name"
                placeholder="Adınız Soyadınız"
                value={formData.full_name}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">
                {formData.user_type === 'salon_owner' ? 'Salon Adı *' : formData.user_type === 'albumcu' ? 'Albüm Firması Adı *' : 'Stüdyo / Firma Adı *'}
              </Label>
              <Input
                id="company_name"
                name="company_name"
                placeholder={formData.user_type === 'salon_owner' ? 'Salon adınız' : formData.user_type === 'albumcu' ? 'Album firması adınız' : 'Stüdyo adınız'}
                value={formData.company_name}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="register-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-posta *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="ornek@email.com"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Şifre *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="En az 6 karakter"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="register-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                name="phone"
                placeholder="05XX XXX XX XX"
                value={formData.phone}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="register-phone-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
              data-testid="register-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kayıt yapılıyor...
                </>
              ) : (
                'Kayıt Ol'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Zaten hesabınız var mı?{' '}
              <Link to="/giris" className="text-primary hover:underline font-medium">
                Giriş Yap
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
